import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { eventSchema } from "@/seo/schema";
import { api } from "@/lib/api";
import "./events.css";
import { dateParts, prettyDate, googleCalendarUrl, mapsUrl } from "./eventUtils";
import { useFadeUp } from "./useFadeUp";
import { RsvpModal } from "./RsvpModal";

const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const openRsvp = (e) => {
    e?.preventDefault?.();
    setRsvpOpen(true);
  };
  const [state, setState] = useState("loading"); // loading | ok | missing
  const ref = useFadeUp(state === "ok" ? event?.id : state);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/events/by-slug/${slug}`);
        if (!cancelled) {
          setEvent(data);
          setState("ok");
        }
      } catch (e) {
        if (!cancelled) setState("missing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="sm-events sm-light sm-section" data-testid="event-loading">
        <div className="max-w-[1240px] mx-auto">
          <p className="sm-label" style={{ opacity: 0.6 }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (state === "missing" || !event) {
    return (
      <div className="sm-events sm-light sm-section" data-testid="event-missing">
        <SEO title="Gathering not found · Shiva Murti Sacred Healing Arts" description="This gathering could not be found." noindex />
        <div className="max-w-[1240px] mx-auto text-center">
          <p className="sm-eyebrow">Not Found</p>
          <h1 className="sm-headline mt-6" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)" }}>
            This gathering<br />
            <span className="sm-city">could not be found.</span>
          </h1>
          <p className="mt-10 max-w-md mx-auto">
            It may have been renamed or removed. See upcoming evenings instead.
          </p>
          <Link to="/events" className="sm-btn sm-btn-ghost mt-10" data-testid="back-to-list">
            Upcoming Socials
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    );
  }

  const dp = dateParts(event.date);
  const calUrl = googleCalendarUrl(event);
  const dirUrl = mapsUrl(event.address);

  return (
    <div className="sm-events" ref={ref} data-testid="event-detail-root">
      <SEO
        path={`/socials/${event.slug}`}
        title={`${event.title} — ${event.city_word} · Shiva Murti Healing Social`}
        description={event.tagline || "An in-person Healing Social with the Shiva Murti team."}
        crumbs={[
          { name: "Home", path: "/" },
          { name: "Events", path: "/events" },
          { name: event.title, path: `/socials/${event.slug}` },
        ]}
      />
      <JsonLd data={eventSchema(event)} />

      {/* Hero */}
      <section className="sm-dark sm-section" data-testid="event-hero">
        <div className="max-w-[1240px] mx-auto">
          <p className="sm-eyebrow sm-fade" data-testid="event-eyebrow">
            In-Person · Healing Social
          </p>
          <h1
            className="sm-headline sm-fade mt-6"
            style={{ fontSize: "clamp(2.8rem, 7vw, 6rem)" }}
            data-testid="event-title"
          >
            {event.title}<br />
            <span className="sm-city">{event.city_word}.</span>
          </h1>
          {event.tagline && (
            <p className="sm-fade mt-10 max-w-xl" style={{ fontSize: "1.1rem" }}>
              {event.tagline}
            </p>
          )}
          <div className="sm-fade mt-14 flex flex-wrap gap-4" data-testid="event-hero-ctas">
            <button
              type="button"
              onClick={openRsvp}
              className="sm-btn sm-btn-primary"
              data-testid="rsvp-cta"
              style={{ background: "#CDB083", color: "#1E1C1A", borderColor: "#CDB083" }}
            >
              Reserve Your Free Spot
              <span aria-hidden>→</span>
            </button>
            <a
              href={calUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sm-btn sm-btn-ghost"
              data-testid="calendar-cta"
            >
              Add to Calendar
              <span aria-hidden>↗</span>
            </a>
          </div>
        </div>
      </section>

      <div className="sm-poleng" aria-hidden="true" />

      {/* Date card bar */}
      <section className="sm-light sm-section" data-testid="event-datebar" style={{ paddingTop: "4rem", paddingBottom: "4rem" }}>
        <div className="max-w-[1240px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-3 sm-fade">
              <div className="sm-datecard">
                <span className="m">{dp.month}</span>
                <span className="d">{dp.day}</span>
                <span className="w">{dp.weekday}</span>
              </div>
            </div>
            <div className="md:col-span-3 sm-fade">
              <p className="sm-label" style={{ color: "#B08D57" }}>Time</p>
              <p className="mt-4" style={{ fontSize: "1.1rem", lineHeight: 1.6 }}>
                {event.start_time}
                {event.end_time ? ` – ${event.end_time}` : ""}
              </p>
            </div>
            <div className="md:col-span-3 sm-fade">
              <p className="sm-label" style={{ color: "#B08D57" }}>Where</p>
              <p className="mt-4" style={{ fontSize: "1.1rem", lineHeight: 1.6 }}>
                {event.venue}
              </p>
              <a
                href={dirUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sm-label mt-3 inline-block"
                style={{ color: "#B08D57", textDecoration: "underline", textUnderlineOffset: "4px" }}
                data-testid="directions-link"
              >
                Get directions ↗
              </a>
            </div>
            <div className="md:col-span-3 sm-fade">
              <p className="sm-label" style={{ color: "#B08D57" }}>Admission</p>
              <p className="mt-4" style={{ fontSize: "1.1rem", lineHeight: 1.6 }}>
                Complimentary
              </p>
              <p className="sm-label mt-3" style={{ opacity: 0.65 }}>
                Limited to {event.capacity} guests
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Evening */}
      <section className="sm-light-2 sm-section" data-testid="event-evening">
        <div className="max-w-[760px] mx-auto">
          <p className="sm-eyebrow sm-fade">The Evening</p>
          <h2
            className="sm-headline sm-fade mt-6"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
          >
            Come as<br />
            <span className="sm-city">you are.</span>
          </h2>
          <div className="sm-fade mt-12 space-y-7">
            <p className="sm-drop-cap" style={{ fontSize: "1.08rem" }}>
              This is not a workshop and it is not a lecture. It is a quiet
              evening with the Shiva Murti team — an introduction to the
              lineage, and an invitation to sit in the circle and receive.
            </p>
            <p style={{ fontSize: "1.08rem" }}>
              There is nothing to prepare and nothing you need to know. Come
              in the clothes you are comfortable in, bring water to be
              blessed, and an open heart. The rest unfolds on its own.
            </p>
          </div>
        </div>
      </section>

      {/* The Flow */}
      <section className="sm-light sm-section" data-testid="event-flow">
        <div className="max-w-[860px] mx-auto">
          <p className="sm-eyebrow sm-fade">The Flow</p>
          <h2
            className="sm-headline sm-fade mt-6"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
          >
            How the night<br />
            <span className="sm-city">unfolds.</span>
          </h2>
          <ol className="sm-flow sm-fade mt-14" data-testid="flow-list">
            {(event.flow || []).map((line, i) => {
              const [title, ...rest] = line.split("—");
              const body = rest.join("—").trim();
              return (
                <li key={i}>
                  <div>
                    <h4>{title.trim()}</h4>
                    {body && <p>{body}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* What to Bring */}
      <section className="sm-dark sm-section" data-testid="event-bring">
        <div className="max-w-[860px] mx-auto">
          <p className="sm-eyebrow sm-fade">What to Bring</p>
          <h2
            className="sm-headline sm-fade mt-6"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
          >
            A few small<br />
            <span className="sm-city">preparations.</span>
          </h2>
          <ul className="sm-fade mt-12 space-y-5" data-testid="bring-list" style={{ listStyle: "none", padding: 0 }}>
            {(event.bring || []).map((item, i) => (
              <li
                key={i}
                style={{
                  fontSize: "1.15rem",
                  lineHeight: 1.6,
                  borderTop: "1px solid rgba(244,236,223,0.18)",
                  paddingTop: "1.1rem",
                }}
              >
                {item}
              </li>
            ))}
          </ul>
          <p className="sm-fade mt-14 sm-label" style={{ color: "#CDB083" }}>
            Hosted by {event.host || "Johnny"}
          </p>
        </div>
      </section>

      <div className="sm-poleng" aria-hidden="true" />

      {/* RSVP */}
      <section className="sm-light sm-section" id="rsvp" data-testid="event-rsvp">
        <div className="max-w-[760px] mx-auto">
          <p className="sm-eyebrow sm-fade text-center">Reserve</p>
          <h2
            className="sm-headline sm-fade mt-6 text-center"
            style={{ fontSize: "clamp(2.1rem, 4.5vw, 3.4rem)" }}
          >
            Save your place<br />
            <span className="sm-city">in the circle.</span>
          </h2>
          <p className="sm-fade mt-10 text-center max-w-lg mx-auto" style={{ fontSize: "1.05rem" }}>
            This evening is our gift to you. Space is intentionally limited to
            keep it intimate, so please reserve ahead.
          </p>

          <div className="sm-fade mt-14 text-center">
            <button
              type="button"
              onClick={openRsvp}
              className="sm-btn sm-btn-primary"
              data-testid="rsvp-section-cta"
            >
              Reserve Your Free Spot
              <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>
      <RsvpModal open={rsvpOpen} onClose={() => setRsvpOpen(false)} />

      {/* Date reminder footer strip */}
      <section className="sm-dark" style={{ padding: "2rem 1.5rem" }} data-testid="event-datestrip">
        <div className="max-w-[1240px] mx-auto flex items-center justify-center gap-6 flex-wrap">
          <p className="sm-label" style={{ color: "#CDB083" }}>
            {prettyDate(event.date)} · {event.start_time} · {event.venue}
          </p>
        </div>
      </section>

      <footer className="sm-dark" style={{ padding: "2.5rem 1.5rem", borderTop: "1px solid rgba(244,236,223,0.12)" }} data-testid="event-footer">
        <div className="max-w-[1240px] mx-auto text-center sm-label" style={{ opacity: 0.75 }}>
          © {new Date().getFullYear()} Shiva Murti Sacred Healing Arts
        </div>
      </footer>
    </div>
  );
};

export default EventDetail;
